<app-catalog-page-card title="INVOICE CONCEPTS">
  <app-catalog-table-toolbar
    datatableId="kt_invoice_concept_datatable"
    searchPlaceholder="Search invoice concept records..."
    addButtonLabel="Add Invoice Concept"
    addModalId="create_invoice_concept_modal"
    refreshAriaLabel="Refresh invoice concepts"
    [disableRefresh]="isLoading()"
    (refresh)="loadInvoiceConcepts()"
  ></app-catalog-table-toolbar>

  <app-catalog-crud-modal
    modalId="create_invoice_concept_modal"
    title="Create Invoice Concept"
    submitLabel="Create Invoice Concept"
    [submitting]="isCreating()"
    [formError]="createFormError()"
    (submit)="onCreateInvoiceConcept()"
    (cancel)="resetCreateInvoiceConceptForm()"
  >
    <div modal-body>
      <form class="grid gap-4" autocomplete="off" [formGroup]="createInvoiceConceptForm">
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-invoice-concept-code">
            Code <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="create-invoice-concept-code"
            type="text"
            class="kt-input"
            maxlength="30"
            formControlName="code"
            [attr.aria-invalid]="shouldShowCreateFieldError('code')"
          />

          @if (shouldShowCreateRequiredError('code')) {
            <div class="catalog-field-error">Code is required.</div>
          }

          @if (shouldShowCreateMaxLengthError('code')) {
            <div class="catalog-field-error">Code cannot exceed 30 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-invoice-concept-name">
            Name <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="create-invoice-concept-name"
            type="text"
            class="kt-input"
            maxlength="100"
            formControlName="name"
            [attr.aria-invalid]="shouldShowCreateFieldError('name')"
          />

          @if (shouldShowCreateRequiredError('name')) {
            <div class="catalog-field-error">Name is required.</div>
          }

          @if (shouldShowCreateMaxLengthError('name')) {
            <div class="catalog-field-error">Name cannot exceed 100 characters.</div>
          }
        </div>
      </form>
    </div>
  </app-catalog-crud-modal>

  <button
    type="button"
    class="hidden"
    aria-hidden="true"
    tabindex="-1"
    data-kt-modal-toggle="#edit_invoice_concept_modal"
  ></button>

  <app-catalog-crud-modal
    modalId="edit_invoice_concept_modal"
    title="Edit Invoice Concept"
    submitLabel="Save Changes"
    [submitting]="isUpdating()"
    [formError]="editFormError()"
    (submit)="onUpdateInvoiceConcept()"
    (cancel)="resetEditInvoiceConceptForm()"
  >
    <div modal-body>
      <form class="grid gap-4" autocomplete="off" [formGroup]="editInvoiceConceptForm">
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-invoice-concept-code">
            Code <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="edit-invoice-concept-code"
            type="text"
            class="kt-input"
            maxlength="30"
            formControlName="code"
            [attr.aria-invalid]="shouldShowEditFieldError('code')"
          />

          @if (shouldShowEditRequiredError('code')) {
            <div class="catalog-field-error">Code is required.</div>
          }

          @if (shouldShowEditMaxLengthError('code')) {
            <div class="catalog-field-error">Code cannot exceed 30 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-invoice-concept-name">
            Name <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="edit-invoice-concept-name"
            type="text"
            class="kt-input"
            maxlength="100"
            formControlName="name"
            [attr.aria-invalid]="shouldShowEditFieldError('name')"
          />

          @if (shouldShowEditRequiredError('name')) {
            <div class="catalog-field-error">Name is required.</div>
          }

          @if (shouldShowEditMaxLengthError('name')) {
            <div class="catalog-field-error">Name cannot exceed 100 characters.</div>
          }
        </div>
      </form>
    </div>
  </app-catalog-crud-modal>

  <button
    type="button"
    class="hidden"
    aria-hidden="true"
    tabindex="-1"
    data-kt-modal-toggle="#delete_invoice_concept_confirm_modal"
  ></button>

  <app-catalog-delete-confirm-modal
    modalId="delete_invoice_concept_confirm_modal"
    title="Remove Invoice Concept"
    message="Are you sure you want to remove this invoice concept?"
    confirmLabel="Remove Invoice Concept"
    [confirming]="deletingInvoiceConceptId() !== null"
    (confirm)="confirmDeleteInvoiceConcept()"
    (cancel)="cancelDeleteInvoiceConcept()"
  ></app-catalog-delete-confirm-modal>

  @if (hasAlert()) {
    <div
      class="kt-alert kt-alert-light"
      [class.kt-alert-success]="alertVariant() === 'success'"
      [class.kt-alert-destructive]="alertVariant() === 'destructive'"
      role="alert"
    >
      <div class="kt-alert-icon">
        <i class="ki-filled ki-information" aria-hidden="true"></i>
      </div>

      <div class="grow">
        <div class="kt-alert-title"><strong>{{ alertTitle() }}</strong></div>

        @if (alertMessage()) {
          <div class="kt-alert-text">{{ alertMessage() }}</div>
        }
      </div>

      <div class="kt-alert-toolbar">
        <div class="kt-alert-actions">
          <button
            type="button"
            class="kt-alert-close"
            aria-label="Dismiss alert"
            (click)="dismissAlert()"
          >
            <i class="ki-filled ki-cross" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  }

  @if (isLoading()) {
    <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading invoice concepts">
      <span class="catalog-loading-spinner" aria-hidden="true"></span>
      <span class="catalog-loading-text">Loading...</span>
    </div>
  }

  @if (!isLoading() && errorMessage()) {
    <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
      <span>{{ errorMessage() }}</span>
      <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="loadInvoiceConcepts()">Retry</button>
    </div>
  }

  @if (!isLoading() && !errorMessage()) {
    <div
      id="kt_invoice_concept_datatable"
      class="kt-card-table"
      data-kt-datatable="true"
      data-kt-datatable-page-size="20"
      data-kt-datatable-state-save="true"
      data-kt-datatable-state-namespace="invoice-concept-datatable"
      (click)="onTableActionClick($event)"
    >
      <div class="kt-table-wrapper kt-scrollable">
        <table class="kt-table" data-kt-datatable-table="true">
          <thead>
            <tr>
              <th scope="col" data-kt-datatable-column="code">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Code</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th scope="col" data-kt-datatable-column="name">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Name</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th
                scope="col"
                data-kt-datatable-column="actions"
                data-kt-datatable-column-sort="false"
                class="text-end"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            @if (invoiceConcepts().length === 0) {
              <tr>
                <td colspan="3" class="text-center text-secondary-foreground py-6">
                  No invoice concepts found.
                </td>
              </tr>
            } @else {
              @for (item of invoiceConcepts(); track item.id) {
                <tr>
                  <td>{{ item.code || '-' }}</td>
                  <td>{{ item.name }}</td>
                  <td class="text-end">
                    <span class="inline-flex gap-2.5">
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        aria-label="Edit invoice concept"
                        data-action="edit-invoice-concept"
                        [attr.data-invoice-concept-id]="item.id"
                        [disabled]="deletingInvoiceConceptId() !== null || isUpdating()"
                      >
                        <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        aria-label="Remove invoice concept"
                        data-action="delete-invoice-concept"
                        [attr.data-invoice-concept-id]="item.id"
                        [disabled]="deletingInvoiceConceptId() !== null"
                      >
                        <i class="ki-filled ki-trash" aria-hidden="true"></i>
                      </button>
                    </span>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
        <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>

        <div class="kt-datatable-length flex items-center gap-3">
          <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
          <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
        </div>
      </div>
    </div>
  }
</app-catalog-page-card>
